import React from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Wallet, ChevronRight } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { formatCurrency } from './constants';

const ExpenseAlertsCard = ({ expenseAlerts }) => {
  const navigate = useNavigate();

  if (!expenseAlerts || (expenseAlerts.overdue_count === 0 && expenseAlerts.due_soon_count === 0)) {
    return null;
  }

  const hasOverdue = expenseAlerts.overdue_count > 0;

  return (
    <Card 
      className={`bg-white border border-slate-200 border-l-4 ${
        hasOverdue ? 'border-l-red-500' : 'border-l-yellow-500'
      } shadow-sm hover:shadow-md transition-shadow duration-300`}
      data-testid="expense-alerts-card"
    >
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-4">
            <div className={`w-12 h-12 rounded-xl ${
              hasOverdue ? 'bg-red-50' : 'bg-yellow-50'
            } flex items-center justify-center`}>
              {hasOverdue ? (
                <AlertTriangle className="w-6 h-6 text-red-500" strokeWidth={1.5} />
              ) : (
                <Wallet className="w-6 h-6 text-yellow-500" strokeWidth={1.5} />
              )}
            </div>
            <div>
              <p className="text-sm text-slate-500 font-medium mb-2">Pagos Próximos a Vencer</p>
              <div className="flex items-center gap-4 mb-3">
                {expenseAlerts.overdue_count > 0 && (
                  <div className="flex items-center gap-2">
                    <Badge className="bg-red-100 text-red-700 border-0">
                      {expenseAlerts.overdue_count} vencidos
                    </Badge>
                    <span className="text-sm font-medium text-red-600">
                      {formatCurrency(expenseAlerts.total_overdue_amount)}
                    </span>
                  </div>
                )}
                {expenseAlerts.due_soon_count > 0 && (
                  <div className="flex items-center gap-2">
                    <Badge className="bg-yellow-100 text-yellow-700 border-0">
                      {expenseAlerts.due_soon_count} por vencer
                    </Badge>
                    <span className="text-sm font-medium text-yellow-600">
                      {formatCurrency(expenseAlerts.total_due_soon_amount)}
                    </span>
                  </div>
                )}
              </div>
              
              {/* Urgent expenses list */}
              {expenseAlerts.urgent_expenses && expenseAlerts.urgent_expenses.length > 0 && (
                <ul className="space-y-2">
                  {expenseAlerts.urgent_expenses.slice(0, 5).map((expense, idx) => (
                    <li 
                      key={idx} 
                      className="flex items-center justify-between text-sm cursor-pointer hover:bg-slate-50 p-2 rounded"
                      onClick={() => navigate('/gastos')}
                    >
                      <div className="flex items-center gap-2">
                        {expense.is_overdue ? (
                          <span className="text-red-600 font-medium">
                            VENCIDO hace {expense.days_overdue} días
                          </span>
                        ) : (
                          <span className="text-yellow-600 font-medium">
                            Vence en {expense.days_until_due} días
                          </span>
                        )}
                        <span className="text-slate-600">- {expense.name}</span>
                      </div>
                      <span className={`font-medium ${expense.is_overdue ? 'text-red-600' : 'text-slate-700'}`}>
                        {formatCurrency(expense.pending_balance)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => navigate('/gastos')}
            className={`${hasOverdue 
              ? 'text-red-600 hover:text-red-700 hover:bg-red-50' 
              : 'text-yellow-600 hover:text-yellow-700 hover:bg-yellow-50'
            }`}
          >
            Ver todos
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default ExpenseAlertsCard;
